import { authFetch, API_BASE, extractError } from "./api";

/**
 * Stream a chat response from the backend via SSE.
 * Returns { fullText, extractedCode, extractedFiles, suggestedName, plan, changeSummary } when done.
 *
 * extractedFiles: scaffold format { 'routes/data.js': '...', 'views/index.html': '...' }
 * extractedCode: monolithic format (single code string) — backward compat
 * plan: structured plan object from FORGE_PLAN marker (complex app creation)
 * changeSummary: one-line past-tense description emitted by Claude via
 *   [FORGE_CHANGE_SUMMARY: ...] marker. Used to label version-history snapshots.
 *
 * @param {Object} options
 * @param {string} options.sessionId
 * @param {string} options.message
 * @param {Array} options.history
 * @param {string} options.integrationName
 * @param {Object} options.attachment - optional { id, type, filename }
 * @param {string} options.layer - optional "data"|"design"|"full" for layer-scoped edits
 * @param {Object} options.dataContract - optional JSON Schema for layer-scoped edits
 * @param {Object} options.sampleData - optional sample data for design layer
 * @param {string} options.endpoint - optional API endpoint override (default: /chat)
 * @param {Function} options.onText - called with accumulated text on each token
 * @param {Function} options.onName - called when Claude suggests an integration name
 * @param {Function} options.onPlan - called when a structured plan is generated
 * @param {Function} options.onSummary - called when Claude emits a change summary
 * @param {Function} options.onError - called on error
 */
export async function streamChat({
  sessionId,
  message,
  history,
  integrationName,
  attachment,
  isEdit = false,
  isAutoFix = false,
  skipRecipeMatch = false,
  layer,
  dataContract,
  sampleData,
  endpoint,
  onText,
  onName,
  onPlan,
  onSummary,
  onError,
}) {
  let res;
  try {
    const url = endpoint ? `${API_BASE}${endpoint}` : `${API_BASE}/chat`;
    res = await authFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        message,
        history,
        integrationName,
        attachment: attachment || undefined,
        isEdit,
        isAutoFix,
        skipRecipeMatch,
        layer: layer || undefined,
        dataContract: dataContract || undefined,
        sampleData: sampleData || undefined,
      }),
    });
  } catch (fetchErr) {
    // `TypeError: Failed to fetch` / `NetworkError` / `ERR_INCOMPLETE_CHUNKED_ENCODING`
    // all surface here. Give the user an actionable hint instead of the raw
    // browser-level error — the most common cause is the backend restarting
    // mid-request (e.g., crashed or intentionally deployed).
    const raw = fetchErr && fetchErr.message ? String(fetchErr.message) : "unknown";
    const looksLikeStreamInterruption = /chunked|network|failed to fetch|connection closed|aborted/i.test(raw);
    if (looksLikeStreamInterruption) {
      throw new Error(
        "The server didn't finish responding — it may have restarted mid-request. " +
        "Try again in a moment. If this keeps happening, check the backend container logs."
      );
    }
    throw new Error(`Network error: ${raw}`);
  }

  if (!res.ok) {
    // Handle non-JSON error responses (e.g., nginx 502/504 HTML pages)
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error(`Server error (${res.status}). The AI service may be busy — please try again.`);
    }
    const err = await res.json().catch(() => ({ error: "Chat request failed" }));
    throw new Error(extractError(err, "Chat request failed"));
  }

  // Accumulated results — shared between initial stream and any reconnect pass.
  let fullText = "";
  let extractedCode = null;
  let extractedFiles = null;
  let suggestedName = null;
  let plan = null;
  let research = null;
  let recipeSuggestion = null;  // Set when backend's matcher fires before Claude
  let changeSummary = null;     // From [FORGE_CHANGE_SUMMARY: ...] — labels version history
  let iterationId = null;       // First SSE event — doubles as the durable job ID

  // Consume an SSE reader, populating the shared accumulators above.
  // Returns true if the stream ended cleanly (saw a `done` event before EOF),
  // false if it was interrupted (pod crash, network drop, etc.).
  async function consumeReader(reader) {
    const decoder = new TextDecoder();
    let buffer = "";
    let sawDoneEvent = false;

    while (true) {
      let readResult;
      try {
        readResult = await reader.read();
      } catch {
        // Mid-stream interruption — signal caller to attempt reconnect.
        return false;
      }
      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6));

          if (event.type === "iteration") {
            iterationId = event.iterationId;
          } else if (event.type === "text") {
            fullText += event.content;
            if (onText) onText(fullText);
          } else if (event.type === "files") {
            extractedFiles = event.content;
          } else if (event.type === "code") {
            extractedCode = event.content;
          } else if (event.type === "name") {
            suggestedName = event.content;
            if (onName) onName(event.content);
          } else if (event.type === "plan") {
            plan = event.content;
            if (onPlan) onPlan(event.content);
          } else if (event.type === "summary") {
            changeSummary = event.content;
            if (onSummary) onSummary(event.content);
          } else if (event.type === "research") {
            research = event.content;
          } else if (event.type === "recipe_suggestion") {
            // Backend matcher identified a curated recipe that fits this prompt.
            // No Claude generation happens; the caller should render a choice card.
            recipeSuggestion = event.match;
          } else if (event.type === "done") {
            sawDoneEvent = true;
          } else if (event.type === "thinking_start" || event.type === "thinking") {
            // Extended thinking events — ignore silently
          } else if (event.type === "error") {
            throw new Error(event.content);
          }
        } catch (parseErr) {
          if (parseErr.message && !parseErr.message.includes("JSON")) {
            throw parseErr;
          }
        }
      }
    }

    return sawDoneEvent;
  }

  const cleanExit = await consumeReader(res.body.getReader());

  // If the stream ended without a `done` event and we have a jobId, try to
  // reconnect to the replay endpoint on a new pod. We pass the current
  // fullText length as `offset` so the replay skips content already rendered.
  if (!cleanExit && iterationId) {
    await new Promise((r) => setTimeout(r, 1500));
    try {
      const resumeRes = await authFetch(
        `${API_BASE}/chat/jobs/${iterationId}?offset=${fullText.length}`
      );
      if (resumeRes.ok) {
        await consumeReader(resumeRes.body.getReader());
      }
    } catch {
      // Reconnect failed — fall through and return whatever was accumulated.
    }
  }

  return { fullText, extractedCode, extractedFiles, suggestedName, plan, research, recipeSuggestion, changeSummary, iterationId };
}
